import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ownerApi, type ShopBrief } from "../api/client";

const STORAGE_KEY = "owner_shop_filter";

interface OwnerScopeContextType {
  isLoading: boolean;
  accessibleShopIds: number[] | null;  // null means "all shops" (DATIUM)
  allShops: ShopBrief[];
  selectedShopIds: number[];           // the ids the user has chosen to view
  effectiveShopIds: number[];          // selected ∩ accessible — what we send to the API
  setSelectedShopIds: (ids: number[]) => void;
}

const OwnerScopeContext = createContext<OwnerScopeContextType | null>(null);

function loadSelection(): number[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === "number")) {
      return parsed;
    }
  } catch {}
  return null;
}

export function OwnerScopeProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [accessibleShopIds, setAccessibleShopIds] = useState<number[] | null>(null);
  const [allShops, setAllShops] = useState<ShopBrief[]>([]);
  const [selectedShopIds, setSelectedShopIdsState] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;
    ownerApi
      .scope()
      .then((scope) => {
        if (cancelled) return;
        setAccessibleShopIds(scope.accessible_shop_ids);
        setAllShops(scope.shops);
        const stored = loadSelection();
        const accessibleSet = new Set(scope.shops.map((s) => s.id));
        // Honor stored selection (trimmed to currently-accessible). Default = none.
        const valid = (stored ?? []).filter((id) => accessibleSet.has(id));
        setSelectedShopIdsState(valid);
      })
      .catch((err) => {
        if (cancelled) return;
        // Endpoint might 403 for non-admins. Leave defaults. Log so the
        // missing-picker case (allShops stays []) isn't a silent void.
        console.warn("[OwnerScope] failed to load /owner/scope:", err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setSelectedShopIds = useCallback((ids: number[]) => {
    setSelectedShopIdsState(ids);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {}
  }, []);

  const effectiveImmediate = useMemo(() => {
    if (accessibleShopIds === null) return selectedShopIds;
    const allow = new Set(accessibleShopIds);
    return selectedShopIds.filter((id) => allow.has(id));
  }, [accessibleShopIds, selectedShopIds]);

  // Debounce the value consumers depend on so a quick sequence of toggles
  // doesn't fire one round-trip per click.
  const [effectiveShopIds, setEffectiveDebounced] = useState<number[]>(effectiveImmediate);
  useEffect(() => {
    const t = setTimeout(() => setEffectiveDebounced(effectiveImmediate), 100);
    return () => clearTimeout(t);
  }, [effectiveImmediate.join(",")]);

  const value = useMemo(
    () => ({
      isLoading,
      accessibleShopIds,
      allShops,
      selectedShopIds,
      effectiveShopIds,
      setSelectedShopIds,
    }),
    [isLoading, accessibleShopIds, allShops, selectedShopIds, effectiveShopIds, setSelectedShopIds]
  );

  return <OwnerScopeContext.Provider value={value}>{children}</OwnerScopeContext.Provider>;
}

export function useOwnerScope() {
  const ctx = useContext(OwnerScopeContext);
  if (!ctx) throw new Error("useOwnerScope must be used within OwnerScopeProvider");
  return ctx;
}
